import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { KnowledgeSource } from '@vendasta/embeddings';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyDataSource } from '@vendasta/galaxy/table';
import { TranslateModule } from '@ngx-translate/core';

import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { KnowledgeSourceTableComponent } from '../knowledge-source-table/knowledge-source-table.component';
import { StaticTableApi } from '../static-table.api';
import {
  KnowledgeBaseDetail,
  KnowledgeBaseGrant,
  OWNED_SENTINEL,
  TableKnowledgeBase,
  TableKnowledgeSource,
} from '../types';

/**
 * The dedicated Knowledge Collection detail page: a description block, a Sources card listing the
 * collection's members with the shared `KnowledgeSourceTableComponent` (the Galaxy-table sources view),
 * and a Shared-with card, gated by ownership / sharing capability. The embedded table doesn't edit
 * sources themselves (`editingEnabled=false`, no raw sources) — source CRUD lives on the Sources tab. It
 * does manage collection *membership*: owned collections get a per-row "Remove from collection" action
 * (`allowRemoveFromCollection`) and the card's "Add sources" button. A borrowed collection is read-only and
 * shows the managed-by banner instead.
 */
@Component({
  selector: 'ai-knowledge-knowledge-base-detail-sources-table',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    GalaxyPageModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    KnowledgeSourceTableComponent,
  ],
  templateUrl: './knowledge-base-detail-sources-table.component.html',
  styleUrls: ['./knowledge-base-detail-sources-table.component.scss'],
})
export class KnowledgeBaseDetailSourcesTableComponent {
  private _detail!: KnowledgeBaseDetail;
  @Input({ required: true }) set detail(value: KnowledgeBaseDetail) {
    this._detail = value;
    // Keep one stable data source (the galaxy table binds it once and ignores later input swaps); update
    // the backing rows and re-pull so a reload after add/remove reflects the new membership.
    this.sourcesRows.set(value.sources);
    this.sourcesDataSource.setSearchTerm(this.sourcesDataSource.state.searchTerm ?? '');
  }
  get detail(): KnowledgeBaseDetail {
    return this._detail;
  }
  /** Surface sharing capability. Independent of KB ownership — a borrowed collection can still be re-shared downstream, so this is NOT ANDed with ownership; only source editing is ownership-gated (see `canEdit`). */
  @Input() canShare = false;
  /** Whether collection membership can be edited (Add / Remove sources). Combined with ownership; false makes an owned collection read-only too (e.g. Business Center). */
  @Input() editingEnabled = true;
  /** When set, the toolbar renders the platform galaxy back button (deterministic URL) instead of the in-app `back` output. */
  @Input() previousPageUrl?: string;
  /** Translate key for the back button's tooltip; rendered through the `translate` pipe. */
  @Input() previousPageTitle?: string;
  /** Drives the page-level loading treatment while a mutation + refetch is in flight (set by the container). */
  @Input() isBusy = false;

  /** Emitted only on surfaces without a `previousPageUrl` (e.g. storybook), where back is in-app state, not navigation. */
  @Output() back = new EventEmitter<void>();
  @Output() share = new EventEmitter<KnowledgeBaseDetail>();
  @Output() addSource = new EventEmitter<KnowledgeBaseDetail>();
  @Output() removeSource = new EventEmitter<{ kb: TableKnowledgeBase; source: TableKnowledgeSource }>();
  @Output() revokeShare = new EventEmitter<KnowledgeBaseGrant>();

  /** Backing rows for the embedded sources table, read live by the data source; updated when `detail` changes. */
  private readonly sourcesRows = signal<TableKnowledgeSource[]>([]);
  /** Stable read-only data source for the embedded sources table (created once; re-pulled on `detail` change). */
  readonly sourcesDataSource = new GalaxyDataSource<TableKnowledgeSource, unknown, unknown>(
    new StaticTableApi(
      () => this.sourcesRows(),
      (s) => `${s.name} ${s.subtitle} ${s.description} ${s.managedBy}`,
      (s, id) => (id === 'name' ? s.name : id === 'lastModified' ? s.lastModified : undefined),
    ),
  );
  /** Empty raw-source map → the embedded table renders as a read-only listing (no edit / refresh / delete). */
  readonly noRawSources = new Map<string, KnowledgeSource>();

  isOwned(): boolean {
    const managedBy = this.detail.kb.managedBy;
    return managedBy === OWNED_SENTINEL || managedBy === '';
  }

  /** Membership edits (Add / Remove sources) require both ownership and an editing-capable surface. */
  canEdit(): boolean {
    return this.isOwned() && this.editingEnabled;
  }

  showSharing(): boolean {
    return this.canShare;
  }

  readonly listLimit = 5;
  readonly showAllShares = signal(false);

  visible<T>(list: T[], expanded: boolean): T[] {
    return expanded ? list : list.slice(0, this.listLimit);
  }

  trackGrantId = (_: number, g: KnowledgeBaseGrant): string => g.grantId;
}
